import React, { forwardRef } from 'react';

import type { InsertTableRowLocation } from '@aline/common';
import { BaseMenuOption } from 'src/editor/toolbars/menus/BaseMenuOption';
import type { CommonMenuItemProps } from 'src/editor/toolbars/menus/types';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

interface InsertTableRowsMenuItemProps extends CommonMenuItemProps {
  numberOfRows: number;
  rowLocation: InsertTableRowLocation;
}

export const InsertTableRowsMenuItem = forwardRef(function InsertTableRowsMenuItem(
  props: InsertTableRowsMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element {
  const { closeRootMenu, numberOfRows, rowLocation, ...rest } = props;
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const isMultipleRows = numberOfRows > 1;
  const label = isMultipleRows
    ? `Insert ${numberOfRows} rows ${rowLocation}`
    : `Insert row ${rowLocation}`;

  function insertRows(): void {
    if (!editingMode || !currentUserId) return closeRootMenu();

    AuthorizedTransforms.insertTableRows(editor, {
      authorId: currentUserId,
      editingMode,
      rowLocation,
    });
    ClientTransforms.focus(editor);
    closeRootMenu();
  }

  return <BaseMenuOption ref={ref} label={label} onClick={insertRows} {...rest} />;
});
